import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeDollarSign,
  BarChart3,
  Bell,
  Inbox,
  Receipt,
  ServerCog,
  FolderSync,
  HeartPulse,
  Upload,
  Wallet,
  BookOpen,
  Building2,
  CheckSquare,
  FileText,
  ClipboardCheck,
  FolderKanban,
  Images,
  UserRoundCheck,
  LayoutDashboard,
  ListChecks,
  MailCheck,
  Menu,
  Package,
  PenTool,
  Search,
  ShieldCheck,
  Sparkles,
  Truck,
  UserCog,
  Users,
  X,
  CalendarDays,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { initials, relative } from '../lib/format';
import { ROLE } from '../lib/labels';

type Counts = { staff: null | { registrations: number; designReview: number; pricingReview: number; samplesOpen: number; samplesFailed: number; photos: number; erpFailing: number; systemProblems?: number; quoteRequests?: number; driveFailing?: number } };

export function Layout() {
  const { user, isStaff, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  const dash = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<Counts>('/dashboard'), enabled: isStaff, staleTime: 60_000 });
  const staff = dash.data?.staff;
  // A staging copy says so on every screen, so nobody does real work in it by mistake.
  const config = useQuery({ queryKey: ['auth-config'], queryFn: () => api.get<{ environmentLabel?: string | null }>('/auth/config'), staleTime: Infinity });
  const envLabel = config.data?.environmentLabel ?? null;

  const link = (to: string, label: string, icon: React.ReactNode, count?: number, end = false) => (
    <NavLink to={to} end={end} className={({ isActive }) => `navlink ${isActive ? 'active' : ''}`}>
      {icon}
      <span>{label}</span>
      {count ? <span className="count">{count}</span> : null}
    </NavLink>
  );

  return (
    <div className="shell">
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <Link to="/" className="brandbar" aria-label="Circdal rep portal home">
          <img src={`${import.meta.env.BASE_URL}brand/circdal-full-white.svg`} alt="Circdal" />
          <span>
            Rep
            <br />
            Portal
          </span>
        </Link>
        <nav aria-label="Main">
          {link('/', 'Dashboard', <LayoutDashboard />, undefined, true)}
          {link('/projects', 'Projects', <FolderKanban />)}
          {link('/design', 'Configurator', <PenTool />)}
          {link('/leads', 'Leads', <Sparkles />)}
          {link('/inquiries', 'Website inquiries', <Inbox />)}
          {link('/firms', 'Firms and contacts', <Building2 />)}
          {link('/tasks', 'My tasks', <CheckSquare />)}
          {link('/samples', 'Samples', <Package />)}
          {link('/documents', 'Documents', <BookOpen />)}
          {link('/gallery', 'Photo gallery', <Images />)}
          {link('/commissions', 'Commissions', <BadgeDollarSign />)}
          {link('/meet', 'Meet the team', <CalendarDays />)}
          {isStaff ? (
            <>
              <div className="navlabel">Circdal staff</div>
              {link('/quotes', 'Quotes', <FileText />, staff?.quoteRequests)}
              {link('/staff/review', 'Submittal review', <ClipboardCheck />, staff ? staff.designReview + staff.pricingReview : undefined)}
              {link('/staff/registrations', 'Registrations', <ShieldCheck />, staff?.registrations)}
              {link('/staff/samples', 'Sample orders', <Truck />, staff ? staff.samplesOpen + staff.samplesFailed : undefined)}
              {link('/staff/gallery', 'Photo approvals', <Images />, staff?.photos)}
              {link('/staff/orders', 'Orders and payments', <Receipt />)}
              {link('/staff/commissions', 'Commission payouts', <Wallet />)}
              {link('/staff/reports', 'Reports', <BarChart3 />)}
              {link('/staff/clients', 'Client access', <UserRoundCheck />)}
            </>
          ) : null}
          {isAdmin ? (
            <>
              <div className="navlabel">Admin</div>
              {link('/admin/users', 'Reps and agencies', <Users />)}
              {link('/admin/notifications', 'Notifications', <MailCheck />)}
              {link('/admin/templates', 'Checklist templates', <ListChecks />)}
              {link('/admin/samples', 'Sample catalog', <Package />)}
              {link('/admin/import', 'Import data', <Upload />)}
              {link('/admin/erp-sync', 'ERP sync', <ServerCog />, staff?.erpFailing || undefined)}
              {link('/admin/drive-sync', 'Drive sync', <FolderSync />, staff?.driveFailing || undefined)}
              {link('/admin/system', 'System and backups', <HeartPulse />, staff?.systemProblems || undefined)}
            </>
          ) : null}
        </nav>
        <div className="sidefoot">
          <div>{user?.agencyName ?? 'Circdal'}</div>
          <div style={{ opacity: 0.7 }}>{user ? ROLE[user.role] : ''}</div>
        </div>
      </aside>
      {open ? <div className="overlay" style={{ zIndex: 65, background: 'rgba(15,43,46,.3)' }} onClick={() => setOpen(false)} /> : null}
      <div className="main">
        <header className="topbar">
          <button className="btn ghost icon menu-btn" aria-label="Open menu" onClick={() => setOpen(true)}>
            <Menu />
          </button>
          <GlobalSearch />
          <div className="spacer" />
          <Notifications />
          <UserMenu name={user?.name ?? ''} email={user?.email ?? ''} onLogout={logout} />
        </header>
        <main id="content" className="main-content">
          {envLabel ? (
            <div className="env-banner" role="note">
              <strong>{envLabel}</strong>: a test copy of the portal. Nothing here reaches clients, reps or the ERP.
            </div>
          ) : null}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * The header search. On a phone there is no room for a usable box beside the menu, the bell and
 * the user menu, so it starts as a search button that opens the box across the whole top bar.
 */
function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  return (
    <>
      <button type="button" className="btn ghost icon search-toggle" aria-label="Search projects and leads" aria-expanded={open} onClick={() => setOpen(true)}>
        <Search />
      </button>
      <form
        className={`search input-group ${open ? 'open' : ''}`}
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim()) {
            setOpen(false);
            navigate(`/projects?q=${encodeURIComponent(q.trim())}&view=table&status=all`);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <Search />
        <input ref={inputRef} className="input" placeholder="Search projects, leads, firms or cities" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search projects and leads" />
        <button type="button" className="btn ghost icon search-close" aria-label="Close search" onClick={() => setOpen(false)}>
          <X />
        </button>
      </form>
    </>
  );
}

type Notification = { id: number; kind: string; title: string; body: string | null; link: string | null; readAt: string | null; createdAt: string };

function Notifications() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ notifications: Notification[]; unread: number }>('/notifications'),
    refetchInterval: 60_000,
  });
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const unread = q.data?.unread ?? 0;
  const markAll = async () => {
    await api.post('/notifications/read', { all: true });
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  return (
    <div className="relative" ref={ref}>
      <button className="btn ghost icon" aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} onClick={() => {
          // Opening the bell shows what is new now, not what was new at the last minute's poll.
          if (!open) void q.refetch();
          setOpen((o) => !o);
        }}
        style={{ position: 'relative' }}
      >
        <Bell />
        {unread ? (
          <span style={{ position: 'absolute', top: 2, right: 2, background: 'var(--orange)', color: '#fff', borderRadius: 999, fontSize: 10, lineHeight: '15px', minWidth: 15, padding: '0 4px', fontFamily: 'var(--font-mono)' }}>
            {unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="menu notif-menu">
          <div className="row between" style={{ padding: '6px 10px' }}>
            <strong>Notifications</strong>
            {unread ? (
              <button className="btn ghost sm" onClick={markAll}>
                Mark all read
              </button>
            ) : null}
          </div>
          {q.data?.notifications.length ? (
            q.data.notifications.map((n) => (
              <button
                key={n.id}
                onClick={async () => {
                  setOpen(false);
                  if (!n.readAt) {
                    await api.post('/notifications/read', { ids: [n.id] });
                    qc.invalidateQueries({ queryKey: ['notifications'] });
                  }
                  if (n.link) navigate(n.link);
                }}
                style={{ display: 'grid', gap: 2, background: n.readAt ? undefined : 'var(--emerald-tint)' }}
              >
                <span style={{ fontWeight: n.readAt ? 400 : 500 }}>{n.title}</span>
                {n.body ? <span className="small muted">{n.body}</span> : null}
                <span className="tiny muted">{relative(n.createdAt)}</span>
              </button>
            ))
          ) : (
            <p className="small muted" style={{ padding: '10px' }}>
              You have no notifications yet.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function UserMenu({ name, email, onLogout }: { name: string; email: string; onLogout: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button className="btn ghost" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} style={{ gap: 8, padding: '4px 8px' }}>
        <span className="avatar">{initials(name)}</span>
        <span className="small user-name" style={{ fontWeight: 500 }}>
          {name}
        </span>
      </button>
      {open ? (
        <div className="menu" role="menu">
          <div className="small muted" style={{ padding: '6px 10px' }}>
            {email}
          </div>
          <button role="menuitem" onClick={() => (setOpen(false), navigate('/account'))}>
            <UserCog size={15} style={{ verticalAlign: '-3px', marginRight: 8 }} />
            Account settings
          </button>
          <button
            role="menuitem"
            onClick={async () => {
              await onLogout();
              navigate('/login');
            }}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
